import { Controller, useForm } from 'react-hook-form';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Swal from 'sweetalert2';
import Flatpickr from 'react-flatpickr';
import { useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import moment from 'moment';
import { useGetMasterDataQuery } from '../../../Redux/services/masterData';
import {
  useEditTaskMutation, useGetTaskIdfromUniqueTaskIdQuery,
  useGetTaskByIdQuery, useDeleteTaskMutation,
} from '../../../Redux/services/task';
import { useGetProjectMilestonesQuery } from '../../../Redux/services/milestone';
import { useGetLoggedUserQuery } from '../../../Redux/services/user';
import TaskComments from './TaskComments';
import TaskActivityLog from './TaskActivityLog';
import TaskLogHours from './TaskLogHours';
import TaskAttachments from './TaskAttachments';
import RecurringTaskBadge from '../RecurringTaskBadge';
import { userStatusConstants, userRoleConstants, configurableModules } from '../../../utils/constant';
import {
  formatEndTimeForDisplay, formatTaskEndDisplay, normalizeEndTimeForApi,
} from '../../../utils/taskDeadline';

const customSelectStyles = {
  placeholder: (provided) => ({
    ...provided,
    fontWeight: '500',
    opacity: '.7',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '1.15rem',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: '0.7rem',
    paddingBottom: '0.7rem',
    borderRadius: '1.15rem',
    backgroundColor: 'var(--bs-dropdown-bg)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'var(--bs-gray-300)',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    fontWeight: '500',
    color: 'var(--bs-gray-700)',
    fontSize: '1.1rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontWeight: '500',
    fontSize: '1.1rem',
    color: 'var(--bs-gray-700)',
  }),
  cursor: 'default',
  option: (styles, { isDisabled, isSelected }) => ({
    ...styles,
    backgroundColor: 'var(--bs-dropdown-bg)',
    color: isSelected ? 'var(--bs-text-gray-800)' : 'var(--bs-gray-700)',
    cursor: isDisabled ? 'not-allowed' : 'default',
    ':hover': {
      backgroundColor: 'var(--bs-component-hover-bg)',
    },
  }),
};

function TaskDetails() {
  const { uniqueId } = useParams();
  const { data: taskIdfromUniqueId } = useGetTaskIdfromUniqueTaskIdQuery({ id: uniqueId });
  const { data: masterData } = useGetMasterDataQuery();
  const userLoggedIn = useSelector((state) => state.user);
  const { data: taskData, error } = useGetTaskByIdQuery({
    id: taskIdfromUniqueId?.data?.id,
  }, { skip: !taskIdfromUniqueId?.data?.id });
  const { data: milestones } = useGetProjectMilestonesQuery(
    taskData?.data?.Project?.id ?? '',
    { skip: !taskData?.data?.Project?.id },
  );
  const [editTask, { isLoading }] = useEditTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const { data: loggedInUser } = useGetLoggedUserQuery();
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState([]);
  const navigate = useNavigate();

  const milestoneOptions = [];
  milestoneOptions.push(
    {
      name: milestones?.data?.todoMilestone?.name,
      id: milestones?.data?.todoMilestone?.id,
    },
  );
  milestones?.data?.inprogressMilestone?.forEach((milestone) => {
    milestoneOptions.push({
      name: milestone?.name,
      id: milestone?.id,
    });
  });
  milestoneOptions.push(
    {
      name: milestones?.data?.completeMilestone?.name,
      id: milestones?.data?.completeMilestone?.id,
    },
  );
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: '',
      project: '',
      milestone: '',
      users: '',
      startDate: '',
      endDate: '',
      endTime: '',
      description: '',
    },
  });
  useEffect(() => {
    if (taskData) {
      reset({
        title: taskData?.data?.title ?? '',
        project: taskData?.data?.Project ? {
          label: taskData.data.Project.name,
          value: taskData.data.Project.id,
        } : '',
        milestone: taskData?.data?.Milestone ? {
          label: taskData.data.Milestone.name,
          value: taskData.data.Milestone.id,
        } : '',
        users: taskData?.data?.taskUsers ? taskData?.data?.taskUsers
          ?.map((taskUser) => ({
            label: taskUser.fullName,
            value: taskUser.id,
          })) : '',
        startDate: moment(taskData?.data?.startDate, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        endDate: moment(taskData?.data?.endDate, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        endTime: formatEndTimeForDisplay(taskData?.data?.endTime) || '',
        description: taskData?.data?.description ?? '',
      });
    }
    // eslint-disable-next-line
  }, [taskData?.data, reset]);

  const onSubmit = async (data) => {
    const {
      users, project, startDate, endDate, endTime, milestone,

    } = data;
    const finalMilestoneId = milestones?.data?.completeMilestone?.id;
    const isMovingToFinalMilestone = milestone?.value === finalMilestoneId
      && taskData?.data?.Milestone?.id !== finalMilestoneId;
    if (taskData?.data?.isFlagged && isMovingToFinalMilestone) {
      const existingDocumentCount = taskData?.data?.Documents?.length ?? 0;
      if (existingDocumentCount === 0 && attachments.length === 0) {
        window.toastr.error('Document is required before changing to the final milestone');
        return;
      }
    }
    const usersId = users.map((user) => user.value);
    const reqBody = {
      ...data,
      users: usersId,
      project: project.value,
      // status: status.value,
      startDate: moment(startDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      endDate: moment(endDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      endTime: normalizeEndTimeForApi(endTime),
      attachmentIds: attachments,
      milestoneId: milestone.value,
    };
    const button = document.getElementById('save-btn');
    button.disabled = true;
    const response = await editTask({ data: reqBody, id: taskIdfromUniqueId?.data?.id });
    button.disabled = false;
    if (response?.data?.success) {
      setAttachments([]);
      setFiles([]);
      window.toastr.success('Task updated successfully!');
    }
    if (response.error) {
      window.toastr.error(response.error.message);
    }
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    Swal.fire({
      text: 'Do you want to delete this task?',
      icon: 'warning',
      iconColor: 'var(--bs-primary)',
      showCancelButton: true,
      heightAuto: false,
      confirmButtonText: 'Yes',
      cancelButtonColor: '#161221',
      customClass: {
        confirmButton: 'btn btn-sm btn-secondary bg-dark',
        cancelButton: 'btn btn-sm btn-outline-secondary ms-3',
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteTask(id);
        navigate('/tasks');
        if (response?.data?.success) {
          window.toastr.success(response.data.message);
        }
      }
    });
  };

  const handleCancel = () => {
    setAttachments([]);
    setFiles([]);
    reset({
      title: taskData?.data?.title ?? '',
      project: taskData?.data?.Project ? {
        label: taskData.data.Project.name,
        value: taskData.data.Project.id,
      } : '',
      // status: taskData?.data?.TaskStatus ? {
      //   label: taskData.data.TaskStatus.name,
      //   value: taskData.data.TaskStatus.id,
      // } : '',
      milestone: taskData?.data?.Milestone ? {
        label: taskData.data.Milestone.name,
        value: taskData.data.Milestone.id,
      } : '',
      users: taskData?.data?.taskUsers ? taskData?.data?.taskUsers?.map((taskUser) => ({
        label: taskUser.fullName,
        value: taskUser.id,
      })) : '',
      startDate: moment(taskData?.data?.startDate, 'YYYY-MM-DD').format('DD-MM-YYYY'),
      endDate: moment(taskData?.data?.endDate, 'YYYY-MM-DD').format('DD-MM-YYYY'),
      endTime: formatEndTimeForDisplay(taskData?.data?.endTime) || '',
      description: taskData?.data?.description ?? '',
    });
  };

  // const renderStatus = () => masterData?.data?.taskStatuses?.map((taskStatus) => ({
  //   label: taskStatus?.name,
  //   value: taskStatus?.id,
  // }));
  const renderMilestone = () => milestoneOptions.map((milestone) => ({
    label: milestone?.name,
    value: milestone?.id,
  }));
  const renderProjects = () => masterData?.data?.projects?.map((project) => ({
    label: project?.name,
    value: project?.id,
  }));
  const renderUsers = () => taskData?.data?.projectUsers
    ?.filter((projectUsers) => projectUsers?.userStatusId === userStatusConstants.ACTIVE)
    .map((projectUsers) => ({
      label: projectUsers?.fullName,
      value: projectUsers?.id,
    }));

  if (error) {
    window.toastr.error('Something went wrong');
  }
  if (!taskData?.data) return null;

  return (
    <>
      <div className="d-flex justify-content-between progress-button mt-5">
        <div>
          <h2 className="pb-0 mb-0 ms-0 pt-0">Task Details</h2>
          <ul className="breadcrumb fw-semibold fs-base mb-1">
            <li className="breadcrumb-item text-muted">
              <Link to="/" className="text-muted text-hover-primary">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item text-muted">
              <Link to="/tasks" className="text-muted text-hover-primary">
                Tasks
              </Link>
            </li>
            <li className="breadcrumb-item text-gray-900">
              Task details
            </li>
          </ul>
        </div>
      </div>
      <div className="row mt-5">
        <div className="col-xl-8 col-lg-12 col-md-12">
          <div className="row">
            <div className="col-xl-12 col-12">
              <div className="card mb-5 mb-xl-10 chart_card">
                <div className="card-body pt-9 pb-0">
                  <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                    <div className="flex-grow-1">
                      <div
                        className="d-flex justify-content-between align-items-start flex-wrap mb-2"
                      >
                        <div className="d-flex flex-column w-100">
                          <div
                            className="d-flex justify-content-between align-items-start flex-wrap mb-0"
                          >
                            <h1 className="fs-2 fw-bold me-1">
                              Task: {taskData?.data?.uniqueTaskId}
                            </h1>
                            {loggedInUser?.data?.RoleId === userRoleConstants.ADMIN
                              && <i className="fa fa-trash" aria-label="Delete Task" title="Delete Task" onClick={(e) => handleDelete(taskData?.data?.id, e)} onKeyDown={(e) => e.key === 'Enter' && handleDelete(taskData?.data?.id, e)} role="button" tabIndex={0} />}
                          </div>
                          <div
                            className="d-flex flex-wrap fw-semibold fs-6 mb-1 pe-2"
                          >
                            <p
                              className="d-flex align-items-center text-hover-primary me-5 mb-2"
                            >
                              <span className="svg-icon svg-icon-4 me-1">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    opacity="0.3"
                                    d="M5 8.04999L11.8 11.95V19.85L5 15.85V8.04999Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M20.1 6.65L12.3 2.15C12 1.95 11.6 1.95 11.3 2.15L3.5 6.65C3.2 6.85 3 7.15 3 7.45V16.45C3 16.75 3.2 17.15 3.5 17.25L11.3 21.75C11.5 21.85 11.6 21.85 11.8 21.85C12 21.85 12.1 21.85 12.3 21.75L20.1 17.25C20.4 17.05 20.6 16.75 20.6 16.45V7.45C20.6 7.15 20.4 6.75 20.1 6.65ZM5 15.85V7.95L11.8 4.05L18.6 7.95L11.8 11.95V19.85L5 15.85Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </span>
                              Group:
                              <OverlayTrigger
                                placement="top"
                                delay={{ show: 250, hide: 300 }}
                                overlay={<Tooltip id="button-tooltip">{taskData?.data?.Project?.name}</Tooltip>}
                              >
                                <span style={{ maxWidth: '250px' }} className="text-truncate">
                                  <strong className="ms-2">  {taskData?.data?.Project?.name}
                                  </strong>
                                </span>
                              </OverlayTrigger>
                            </p>
                            <p
                              className="d-flex align-items-center text-hover-primary mb-2 me-5"
                            >

                              <span className="svg-icon svg-icon-4 me-1">
                                <svg
                                  width="15"
                                  height="18"
                                  viewBox="0 0 25 28"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M24.0259 11.4401H1.97259C1.69436 11.4505 1.43123 11.5693 1.2394 11.7711C1.04757 11.9729 0.942247 12.2417 0.945922 12.5201V20.0801C0.933592 21.0248 1.10836 21.9627 1.46016 22.8395C1.81196 23.7164 2.33382 24.515 2.99568 25.1892C3.65754 25.8635 4.4463 26.4001 5.3165 26.7681C6.1867 27.1361 7.12112 27.3282 8.06592 27.3334H17.9993C19.8855 27.288 21.6778 26.5012 22.988 25.1436C24.2983 23.7859 25.0208 21.9667 24.9993 20.0801V12.5201C25.0037 12.2504 24.9057 11.989 24.7251 11.7886C24.5445 11.5882 24.2947 11.4637 24.0259 11.4401ZM8.73259 21.8401C8.51017 21.84 8.29271 21.7744 8.1073 21.6515C7.92189 21.5287 7.77672 21.354 7.68989 21.1492C7.60306 20.9444 7.5784 20.7186 7.61899 20.5C7.65957 20.2813 7.76361 20.0794 7.91813 19.9194C8.07266 19.7594 8.27084 19.6484 8.48798 19.6003C8.70513 19.5522 8.93164 19.569 9.1393 19.6487C9.34695 19.7283 9.52658 19.8673 9.65578 20.0484C9.78499 20.2294 9.85807 20.4445 9.86592 20.6668C9.86241 20.965 9.74146 21.2499 9.5293 21.4595C9.31714 21.6692 9.03087 21.7868 8.73259 21.7868V21.8401ZM8.73259 17.5868C8.50844 17.5868 8.28932 17.5203 8.10294 17.3958C7.91657 17.2712 7.77131 17.0942 7.68553 16.8871C7.59975 16.6801 7.5773 16.4522 7.62103 16.2323C7.66476 16.0125 7.7727 15.8105 7.9312 15.652C8.0897 15.4935 8.29164 15.3856 8.51149 15.3419C8.73133 15.2981 8.95921 15.3206 9.1663 15.4064C9.37339 15.4921 9.55039 15.6374 9.67492 15.8238C9.79945 16.0102 9.86592 16.2293 9.86592 16.4534C9.86771 16.6028 9.83962 16.7509 9.7833 16.8892C9.72697 17.0276 9.64356 17.1532 9.53796 17.2588C9.43236 17.3644 9.30672 17.4478 9.1684 17.5041C9.03009 17.5605 8.88192 17.5886 8.73259 17.5868ZM12.9993 21.8401C12.701 21.8331 12.4175 21.7088 12.2104 21.4941C12.0032 21.2794 11.889 20.9917 11.8926 20.6934C11.8926 20.3964 12.0106 20.1115 12.2206 19.9015C12.4307 19.6914 12.7155 19.5734 13.0126 19.5734C13.3096 19.5734 13.5945 19.6914 13.8045 19.9015C14.0146 20.1115 14.1326 20.3964 14.1326 20.6934C14.1291 20.9917 14.0081 21.2765 13.796 21.4862C13.5838 21.6959 13.2975 21.8135 12.9993 21.8134V21.8401ZM12.9993 17.5868C12.701 17.5798 12.4175 17.4555 12.2104 17.2408C12.0032 17.0261 11.889 16.7384 11.8926 16.4401C11.8926 16.1431 12.0106 15.8582 12.2206 15.6481C12.4307 15.4381 12.7155 15.3201 13.0126 15.3201C13.3096 15.3201 13.5945 15.4381 13.8045 15.6481C14.0146 15.8582 14.1326 16.1431 14.1326 16.4401C14.1326 16.7384 14.015 17.0246 13.8054 17.2368C13.5957 17.449 13.3109 17.5699 13.0126 17.5734L12.9993 17.5868ZM17.2393 21.8401C16.9387 21.8401 16.6504 21.7207 16.4379 21.5082C16.2253 21.2956 16.1059 21.0073 16.1059 20.7068C16.1059 20.4062 16.2253 20.1179 16.4379 19.9054C16.6504 19.6928 16.9387 19.5734 17.2393 19.5734C17.5398 19.5734 17.8281 19.6928 18.0406 19.9054C18.2532 20.1179 18.3726 20.4062 18.3726 20.7068C18.3726 21.0073 18.2532 21.2956 18.0406 21.5082C17.8281 21.7207 17.5398 21.8401 17.2393 21.8401ZM17.2393 17.5868C16.9387 17.5868 16.6504 17.4674 16.4379 17.2548C16.2253 17.0423 16.1059 16.754 16.1059 16.4534C16.1059 16.1529 16.2253 15.8646 16.4379 15.652C16.6504 15.4395 16.9387 15.3201 17.2393 15.3201C17.5398 15.3201 17.8281 15.4395 18.0406 15.652C18.2532 15.8646 18.3726 16.1529 18.3726 16.4534C18.3726 16.754 18.2532 17.0423 18.0406 17.2548C17.8281 17.4674 17.5398 17.5868 17.2393 17.5868ZM24.6393 8.13343C24.7349 8.40774 24.7203 8.7085 24.5984 8.9722C24.4765 9.2359 24.2569 9.44192 23.9859 9.54677C23.8703 9.58813 23.7487 9.61063 23.6259 9.61343H2.62592C2.2723 9.61343 1.93316 9.47296 1.68311 9.22291C1.43306 8.97286 1.29259 8.63372 1.29259 8.2801C1.28883 8.11525 1.32066 7.95153 1.38592 7.8001C1.77683 6.84295 2.37003 5.98161 3.12487 5.27509C3.87972 4.56858 4.77837 4.03358 5.75926 3.70677V1.62677C5.75926 1.3863 5.85478 1.15569 6.02481 0.985655C6.19485 0.815622 6.42546 0.720099 6.66592 0.720099C6.90639 0.720099 7.137 0.815622 7.30703 0.985655C7.47707 1.15569 7.57259 1.3863 7.57259 1.62677V3.33343H12.3059V1.62677C12.2904 1.49938 12.3021 1.37015 12.3402 1.24761C12.3783 1.12508 12.442 1.01204 12.5271 0.915961C12.6122 0.819883 12.7167 0.74296 12.8337 0.690277C12.9507 0.637594 13.0776 0.610352 13.2059 0.610352C13.3343 0.610352 13.4611 0.637594 13.5781 0.690277C13.6952 0.74296 13.7997 0.819883 13.8847 0.915961C13.9698 1.01204 14.0335 1.12508 14.0716 1.24761C14.1098 1.37015 14.1215 1.49938 14.1059 1.62677V3.33343H18.3326V1.62677C18.3171 1.49938 18.3287 1.37015 18.3669 1.24761C18.405 1.12508 18.4687 1.01204 18.5538 0.915961C18.6389 0.819883 18.7434 0.74296 18.8604 0.690277C18.9774 0.637594 19.1043 0.610352 19.2326 0.610352C19.3609 0.610352 19.4878 0.637594 19.6048 0.690277C19.7218 0.74296 19.8263 0.819883 19.9114 0.915961C19.9965 1.01204 20.0602 1.12508 20.0983 1.24761C20.1364 1.37015 20.1481 1.49938 20.1326 1.62677V3.70677C21.1713 4.05261 22.1173 4.63121 22.8984 5.39839C23.6794 6.16557 24.2749 7.10105 24.6393 8.13343Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </span>
                              Start Date:
                              <strong className="ms-2">{taskData?.data?.Project?.startDate ? moment(taskData?.data?.Project?.startDate, 'YYYY-MM-DD').format('DD MMM YY') : '---'}
                              </strong>
                            </p>
                            <p
                              className="d-flex align-items-center text-hover-primary mb-2 me-5"
                            >

                              <span className="svg-icon svg-icon-4 me-1">
                                <svg
                                  width="15"
                                  height="18"
                                  viewBox="0 0 25 28"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M24.0259 11.4401H1.97259C1.69436 11.4505 1.43123 11.5693 1.2394 11.7711C1.04757 11.9729 0.942247 12.2417 0.945922 12.5201V20.0801C0.933592 21.0248 1.10836 21.9627 1.46016 22.8395C1.81196 23.7164 2.33382 24.515 2.99568 25.1892C3.65754 25.8635 4.4463 26.4001 5.3165 26.7681C6.1867 27.1361 7.12112 27.3282 8.06592 27.3334H17.9993C19.8855 27.288 21.6778 26.5012 22.988 25.1436C24.2983 23.7859 25.0208 21.9667 24.9993 20.0801V12.5201C25.0037 12.2504 24.9057 11.989 24.7251 11.7886C24.5445 11.5882 24.2947 11.4637 24.0259 11.4401ZM8.73259 21.8401C8.51017 21.84 8.29271 21.7744 8.1073 21.6515C7.92189 21.5287 7.77672 21.354 7.68989 21.1492C7.60306 20.9444 7.5784 20.7186 7.61899 20.5C7.65957 20.2813 7.76361 20.0794 7.91813 19.9194C8.07266 19.7594 8.27084 19.6484 8.48798 19.6003C8.70513 19.5522 8.93164 19.569 9.1393 19.6487C9.34695 19.7283 9.52658 19.8673 9.65578 20.0484C9.78499 20.2294 9.85807 20.4445 9.86592 20.6668C9.86241 20.965 9.74146 21.2499 9.5293 21.4595C9.31714 21.6692 9.03087 21.7868 8.73259 21.7868V21.8401ZM8.73259 17.5868C8.50844 17.5868 8.28932 17.5203 8.10294 17.3958C7.91657 17.2712 7.77131 17.0942 7.68553 16.8871C7.59975 16.6801 7.5773 16.4522 7.62103 16.2323C7.66476 16.0125 7.7727 15.8105 7.9312 15.652C8.0897 15.4935 8.29164 15.3856 8.51149 15.3419C8.73133 15.2981 8.95921 15.3206 9.1663 15.4064C9.37339 15.4921 9.55039 15.6374 9.67492 15.8238C9.79945 16.0102 9.86592 16.2293 9.86592 16.4534C9.86771 16.6028 9.83962 16.7509 9.7833 16.8892C9.72697 17.0276 9.64356 17.1532 9.53796 17.2588C9.43236 17.3644 9.30672 17.4478 9.1684 17.5041C9.03009 17.5605 8.88192 17.5886 8.73259 17.5868ZM12.9993 21.8401C12.701 21.8331 12.4175 21.7088 12.2104 21.4941C12.0032 21.2794 11.889 20.9917 11.8926 20.6934C11.8926 20.3964 12.0106 20.1115 12.2206 19.9015C12.4307 19.6914 12.7155 19.5734 13.0126 19.5734C13.3096 19.5734 13.5945 19.6914 13.8045 19.9015C14.0146 20.1115 14.1326 20.3964 14.1326 20.6934C14.1291 20.9917 14.0081 21.2765 13.796 21.4862C13.5838 21.6959 13.2975 21.8135 12.9993 21.8134V21.8401ZM12.9993 17.5868C12.701 17.5798 12.4175 17.4555 12.2104 17.2408C12.0032 17.0261 11.889 16.7384 11.8926 16.4401C11.8926 16.1431 12.0106 15.8582 12.2206 15.6481C12.4307 15.4381 12.7155 15.3201 13.0126 15.3201C13.3096 15.3201 13.5945 15.4381 13.8045 15.6481C14.0146 15.8582 14.1326 16.1431 14.1326 16.4401C14.1326 16.7384 14.015 17.0246 13.8054 17.2368C13.5957 17.449 13.3109 17.5699 13.0126 17.5734L12.9993 17.5868ZM17.2393 21.8401C16.9387 21.8401 16.6504 21.7207 16.4379 21.5082C16.2253 21.2956 16.1059 21.0073 16.1059 20.7068C16.1059 20.4062 16.2253 20.1179 16.4379 19.9054C16.6504 19.6928 16.9387 19.5734 17.2393 19.5734C17.5398 19.5734 17.8281 19.6928 18.0406 19.9054C18.2532 20.1179 18.3726 20.4062 18.3726 20.7068C18.3726 21.0073 18.2532 21.2956 18.0406 21.5082C17.8281 21.7207 17.5398 21.8401 17.2393 21.8401ZM17.2393 17.5868C16.9387 17.5868 16.6504 17.4674 16.4379 17.2548C16.2253 17.0423 16.1059 16.754 16.1059 16.4534C16.1059 16.1529 16.2253 15.8646 16.4379 15.652C16.6504 15.4395 16.9387 15.3201 17.2393 15.3201C17.5398 15.3201 17.8281 15.4395 18.0406 15.652C18.2532 15.8646 18.3726 16.1529 18.3726 16.4534C18.3726 16.754 18.2532 17.0423 18.0406 17.2548C17.8281 17.4674 17.5398 17.5868 17.2393 17.5868ZM24.6393 8.13343C24.7349 8.40774 24.7203 8.7085 24.5984 8.9722C24.4765 9.2359 24.2569 9.44192 23.9859 9.54677C23.8703 9.58813 23.7487 9.61063 23.6259 9.61343H2.62592C2.2723 9.61343 1.93316 9.47296 1.68311 9.22291C1.43306 8.97286 1.29259 8.63372 1.29259 8.2801C1.28883 8.11525 1.32066 7.95153 1.38592 7.8001C1.77683 6.84295 2.37003 5.98161 3.12487 5.27509C3.87972 4.56858 4.77837 4.03358 5.75926 3.70677V1.62677C5.75926 1.3863 5.85478 1.15569 6.02481 0.985655C6.19485 0.815622 6.42546 0.720099 6.66592 0.720099C6.90639 0.720099 7.137 0.815622 7.30703 0.985655C7.47707 1.15569 7.57259 1.3863 7.57259 1.62677V3.33343H12.3059V1.62677C12.2904 1.49938 12.3021 1.37015 12.3402 1.24761C12.3783 1.12508 12.442 1.01204 12.5271 0.915961C12.6122 0.819883 12.7167 0.74296 12.8337 0.690277C12.9507 0.637594 13.0776 0.610352 13.2059 0.610352C13.3343 0.610352 13.4611 0.637594 13.5781 0.690277C13.6952 0.74296 13.7997 0.819883 13.8847 0.915961C13.9698 1.01204 14.0335 1.12508 14.0716 1.24761C14.1098 1.37015 14.1215 1.49938 14.1059 1.62677V3.33343H18.3326V1.62677C18.3171 1.49938 18.3287 1.37015 18.3669 1.24761C18.405 1.12508 18.4687 1.01204 18.5538 0.915961C18.6389 0.819883 18.7434 0.74296 18.8604 0.690277C18.9774 0.637594 19.1043 0.610352 19.2326 0.610352C19.3609 0.610352 19.4878 0.637594 19.6048 0.690277C19.7218 0.74296 19.8263 0.819883 19.9114 0.915961C19.9965 1.01204 20.0602 1.12508 20.0983 1.24761C20.1364 1.37015 20.1481 1.49938 20.1326 1.62677V3.70677C21.1713 4.05261 22.1173 4.63121 22.8984 5.39839C23.6794 6.16557 24.2749 7.10105 24.6393 8.13343Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </span>
                              End Date:
                              <strong className="ms-2">
                                {formatTaskEndDisplay(taskData.data.endDate, taskData.data.endTime)}
                              </strong>
                            </p>
                            {taskData?.data?.isRecurringTask && (
                            <p
                              className="d-flex align-items-center text-hover-primary mb-2 me-5"
                            >
                              <span className="svg-icon svg-icon-4 me-1">
                                <i className="fa fa-repeat" aria-hidden="true" />
                              </span>
                              Recurring:
                              <strong className="ms-2">
                                {taskData?.data?.RecurringTaskFrequency?.name || 'Yes'}
                              </strong>
                            </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <form className="row gy-5 g-xl-10" onSubmit={handleSubmit(onSubmit)}>
            <div className="col-xl-12">
              <div className="card card-flush mb-6 mb-xl-9 card1">
                <div className="card-header mt-6 minimize">
                  <div className="card-title flex-column">
                    <h2 className="mb-1">Task Info</h2>
                    <div className="fs-6 fw-semibold text-muted">All relevant
                      task fields
                    </div>
                  </div>
                  <div className="card-toolbar mt-0">
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-light-primary justify-content-center"
                      aria-label="Remove"
                    >
                      <i className="fas fa-minus" />
                    </button>
                  </div>
                </div>
                <div id="chart_info" className="card-body flex-column">
                  <div className="d-flex flex-column gap-5 gap-md-7 mb-5">
                    <div className="row">
                      <div className="col-md-4 fv-row">
                        <label
                          htmlFor="title"
                          className="fs-6 fw-semibold form-label mb-2 assign-to"
                        >Title
                        </label>
                        <input
                          {...register('title', {
                            validate: (value) => {
                              const trimmed = (value || '').trim();
                              if (trimmed.length > 0 && trimmed.length < 3) {
                                return "Title can't be less than 3 characters";
                              }
                              if (trimmed.length > 100) {
                                return "Title can't be more then 100 characters";
                              }
                              return true;
                            },
                          })}
                          id="title"
                          placeholder="Title"
                          className="form-control"
                        />
                        {errors?.title && <div className="text-danger small"> {errors.title?.message} </div>}
                      </div>
                      <div className="col-md-4 fv-row mb-5">
                        <label
                          htmlFor="project"
                          className="required fs-6 fw-semibold form-label mb-2 assign-to"
                        >Group
                        </label>
                        <Controller
                          name="project"
                          rules={{
                            required: { value: true, message: 'Required' },
                          }}
                          control={control}
                          render={({ field }) => (
                            <Select
                              isDisabled
                              {...field}
                              components={{
                                IndicatorSeparator: () => null,
                                DropdownIndicator: () => null,
                              }}
                              options={renderProjects()}
                              placeholder="Group"
                              styles={customSelectStyles}
                            />
                          )}
                        />
                        {errors?.project
                          && <div className="text-danger small"> {errors.project?.message} </div>}
                      </div>
                      {/* <div className="col-md-4 fv-row mb-5">

                        <label
                          htmlFor="status"
                          className="required fs-6 fw-semibold form-label mb-2 assign-to"
                        >Status
                        </label>
                        <Controller
                          name="status"
                          rules={{
                            required: { value: true, message: 'Required' },
                          }}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              components={{ IndicatorSeparator: () => null }}
                              options={renderStatus()}
                              placeholder="Status"
                              styles={customSelectStyles}
                            />
                          )}
                        />
                        {errors?.status
                          && <div className="text-danger small"> {errors.status?.message} </div>}
                      </div> */}
                      <div className="col-md-4 fv-row mb-5">

                        <label
                          htmlFor="Milestone"
                          className="required fs-6 fw-semibold form-label mb-2 assign-to"
                        >Milestone
                        </label>
                        <Controller
                          name="milestone"
                          rules={{
                            required: { value: true, message: 'Required' },
                          }}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              components={{ IndicatorSeparator: () => null }}
                              options={renderMilestone()}
                              placeholder="Milestone"
                              styles={customSelectStyles}
                              isLoading={!milestones}
                            />
                          )}
                        />
                        {errors?.milestone
                          && <div className="text-danger small"> {errors.milestone?.message} </div>}
                      </div>
                      <div className="col-md-4 fv-row mb-5">

                        <label
                          htmlFor="users"
                          className="fs-6 fw-semibold form-label mb-2 assign-to"
                        >Users
                        </label>
                        <Controller
                          name="users"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              isMulti
                              isClearable
                              components={{ IndicatorSeparator: () => null }}
                              options={renderUsers()}
                              placeholder="Users"
                              styles={customSelectStyles}
                            />
                          )}
                        />
                        {errors?.users
                          && <div className="text-danger small"> {errors.users?.message} </div>}
                      </div>
                      <div className="col-md-4 fv-row">

                        <label
                          htmlFor="startDate"
                          className="required fs-6 fw-semibold form-label mb-2 assign-to"
                        >Start
                          date
                        </label>
                        <Controller
                          control={control}
                          name="startDate"
                          render={({ field: { onChange, value } }) => (
                            <Flatpickr
                              value={value}
                              options={{
                                dateFormat: 'd/m/Y',
                                minDate: moment(taskData?.data?.Project?.startDate).format('DD-MM-YYYY'),
                              }}
                              onChange={(_, currentDateString) => onChange(currentDateString)}
                              className="form-control"
                              placeholder="Start Date"
                            />
                          )}
                          rules={{
                            required: { value: true, message: 'required' },
                          }}
                        />
                        {errors?.startDate
                          && <div className="text-danger small"> {errors.startDate?.message} </div>}
                      </div>
                      <div className="col-md-4 fv-row">

                        <label
                          htmlFor="endDate"
                          className="required fs-6 fw-semibold form-label mb-2 assign-to"
                        >End
                          date
                        </label>
                        <Controller
                          name="endDate"
                          control={control}
                          render={({
                            field: {
                              onChange, value,
                            },
                          }) => (
                            <Flatpickr
                              value={value}
                              options={{
                                dateFormat: 'd/m/Y',
                                minDate: taskData?.data?.Project?.startDate,
                              }}
                              onChange={(_, currentDateString) => onChange(currentDateString)}
                              className="form-control"
                              placeholder="End Date"
                            />
                          )}
                          rules={{
                            required: { value: true, message: 'Required' },
                          }}
                        />
                        {errors?.endDate
                          && <div className="text-danger small"> {errors.endDate?.message} </div>}
                      </div>
                      <div className="col-md-4 fv-row">
                        <label
                          htmlFor="endTime"
                          className="fs-6 fw-semibold form-label mb-2 assign-to"
                        >
                          End time (optional)
                        </label>
                        <Controller
                          name="endTime"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Flatpickr
                              value={value}
                              options={{
                                enableTime: true,
                                noCalendar: true,
                                dateFormat: 'H:i',
                                time_24hr: true,
                              }}
                              onChange={(_, currentTimeString) => onChange(currentTimeString)}
                              className="form-control"
                              placeholder="End Time"
                            />
                          )}
                        />
                      </div>
                      <div className="col-xl-12 col-lg-12 col-12">
                        <label htmlFor="description" className="form-label">Description</label>
                        <textarea
                          {...register('description', {
                            maxLength: { value: 500, message: "Description can't be more then 500 characters" },
                          })}
                          cols="30"
                          rows="3"
                          className="form-control"
                          placeholder="Description"
                        />
                        {errors?.description
                          && <div className="text-danger small"> {errors.description?.message} </div>}
                      </div>
                      {taskData?.data?.isRecurringTask && (
                      <div className="col-md-4 fv-row">
                        <div className="fs-6 fw-semibold form-label mb-2 assign-to">
                          Recurring
                        </div>
                        <RecurringTaskBadge task={taskData.data} />
                      </div>
                      )}
                    </div>

                  </div>

                </div>
              </div>
              <TaskAttachments
                setAttachments={setAttachments}
                taskData={taskData?.data}
                files={files}
                setFiles={setFiles}
              />
              <div className="d-flex justify-content-start ms-5 mb-6">
                <button
                  type="submit"
                  id="save-btn"
                  className="btn btn-sm fw-bold btn-secondary me-2"
                >{!isLoading ? <span className="indicator-label">Save</span>
                  : (
                    <span>
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  type="button"
                  className="btn btn-sm btn-outline-secondary fw-bold btn-active-light-primary"
                  data-kt-search-element="preferences-dismiss"
                >Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
        <div className="col-xl-4 col-lg-12 col-md-12">
          <div className="row">
            <TaskComments taskId={taskData?.data?.id} />
            <TaskActivityLog
              id={taskData?.data?.id}
            />
            {(userLoggedIn?.client?.features?.[configurableModules.TIMESHEET])
              && <TaskLogHours taskData={taskData?.data} />}
          </div>
        </div>
      </div>
    </>
  );
}

export default TaskDetails;
